import { useMemo } from 'react'
import type { DesignPersonalizationContext } from '@vp/personalization-types'
import { useSchemesStore } from '../store/SchemesProvider'
import { useSchemeContext } from './useSchemeContext'
import { useSchemeImages } from './useSchemeImages'
import { useSchemeText } from './useSchemeText'

interface UseSchemePreviewDataResult {
  contextId?: string
  contextData?: DesignPersonalizationContext
  hasImages: boolean
  hasText: boolean
  isUpdating: boolean
}

export const useSchemePreviewData = (schemeId: string): UseSchemePreviewDataResult => {
  const { state } = useSchemesStore()
  const scheme = state.schemes[schemeId]

  const isUpdating = useMemo(() => Boolean(scheme?.isUpdating), [scheme?.isUpdating])
  const { contextId, contextData } = useSchemeContext(schemeId)
  const { hasImages } = useSchemeImages(schemeId)
  const { hasText } = useSchemeText(schemeId)

  return {
    contextId,
    contextData,
    hasImages,
    hasText,
    isUpdating,
  }
}

export const useActiveSchemePreviewData = (): UseSchemePreviewDataResult => {
  const { state } = useSchemesStore()
  const activeId = state.activeSchemeId

  const previewData = useSchemePreviewData(activeId ?? '')

  // Allows for Schemes to be active without data yet
  const isUpdating = previewData.isUpdating || !!(activeId && (!previewData.contextId || !previewData.contextData))

  return {
    ...previewData,
    isUpdating,
  }
}
